import { ExpandMore, AccountBalance, ErrorOutline } from '@mui/icons-material';
import { Button } from 'finara-components';
import { useState } from 'react';

import { useOrganizationAccounts } from '@/hooks/queries/useAccounts';
import type { Account } from '@/types/accounts';
import type { PaymentSource } from '@/types/payment';

interface FundSourceProps {
  onSelect: (source: PaymentSource) => void;
}

export const FundSource: React.FC<FundSourceProps> = ({ onSelect }) => {
  const { data: accounts, isLoading, error } = useOrganizationAccounts('active');
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false);

  const organizationAccounts = accounts?.filter(account => account.user_id === null) || [];

  const handleAccountSelect = (account: Account) => {
    setSelectedAccount(account);
    setIsAccountDropdownOpen(false);
  };

  const handleNext = () => {
    if (selectedAccount) {
      const source: PaymentSource = {
        clientId: selectedAccount.organization_id,
        clientName: selectedAccount.organization?.name || 'Organización',
        selectedAccount,
      };
      onSelect(source);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(amount);
  };

  const getAccountTypeName = (type: string) => {
    const typeMap: Record<string, string> = {
      organization: 'Cuenta Principal',
      savings_organization: 'Cuenta de Ahorros',
      payroll: 'Nómina',
      savings: 'Ahorros',
    };
    return typeMap[type] || type;
  };

  const header = (
    <div className="mb-8">
      <h2 className="text-2xl font-bold text-brand-white mb-2">Origen de Fondos</h2>
      <p className="text-muted">Selecciona la cuenta de origen para el pago</p>
    </div>
  );

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto">
        {header}
        <div className="bg-surface border border-border rounded-lg p-8 text-center text-muted">
          Cargando cuentas...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        {header}
        <div className="bg-surface border border-border rounded-lg p-8 flex items-center justify-center space-x-3 text-red-400">
          <ErrorOutline />
          <span>Error al cargar las cuentas. Intenta nuevamente.</span>
        </div>
      </div>
    );
  }

  if (organizationAccounts.length === 0) {
    return (
      <div className="max-w-4xl mx-auto">
        {header}
        <div className="bg-surface border border-border rounded-lg p-8 text-center">
          <ErrorOutline className="text-muted mb-3 mx-auto" fontSize="large" />
          <p className="text-muted">
            No se encontraron cuentas organizacionales activas. Contacta al administrador.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {header}

      {/* Account Selection */}
      <div className="mb-8">
        <label htmlFor="account-dropdown" className="block text-sm font-medium text-brand-white mb-3">
          Cuenta de Origen
        </label>

        <div className="relative">
          <button
            id="account-dropdown"
            type="button"
            onClick={() => setIsAccountDropdownOpen(!isAccountDropdownOpen)}
            className="w-full bg-surface border border-border rounded-lg px-4 py-3 text-left focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
          >
            <div className="flex items-center justify-between">
              <span className="text-muted">
                {selectedAccount
                  ? `${selectedAccount.organization?.name || getAccountTypeName(selectedAccount.type)} - ${selectedAccount.clabe}`
                  : 'Seleccionar cuenta...'}
              </span>
              <ExpandMore className="text-muted" />
            </div>
          </button>

          {isAccountDropdownOpen && (
            <div className="absolute z-10 w-full mt-1 bg-surface border border-border rounded-lg shadow-2xl shadow-black/60 max-h-96 overflow-y-auto">
              {organizationAccounts.map(account => (
                <button
                  key={account.id}
                  onClick={() => handleAccountSelect(account)}
                  className="w-full px-4 py-4 text-left hover:bg-black/5 dark:hover:bg-white/5 first:rounded-t-lg last:rounded-b-lg border-b border-border last:border-b-0"
                >
                  <div className="space-y-2">
                    <div className="font-medium text-brand-white">
                      {account.organization?.name || getAccountTypeName(account.type)}
                    </div>
                    <div className="text-sm text-muted">
                      {getAccountTypeName(account.type)} • {account.clabe} • {account.currency}
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-green-400 font-medium">
                        Disponible: {formatCurrency(account.balance)}
                      </span>
                      <span className="text-xs text-muted">
                        {account.status === 'active' ? 'Activa' : 'Inactiva'}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Selected Account Details */}
      {selectedAccount && (
        <div className="bg-surface border border-border rounded-lg p-6 mb-8">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <AccountBalance className="text-brand-blue" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-brand-white mb-1">
                {selectedAccount.organization?.name || getAccountTypeName(selectedAccount.type)}
              </h3>
              <p className="text-sm text-muted mb-2">{getAccountTypeName(selectedAccount.type)}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-muted">Número de apartado</div>
                  <div className="text-sm font-medium text-brand-white font-mono">
                    {selectedAccount.clabe}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted">Moneda</div>
                  <div className="text-sm font-medium text-brand-white">
                    {selectedAccount.currency}
                  </div>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-green-500/10 border border-green-500/30 p-3 rounded-lg">
                  <div className="text-sm text-muted">Saldo Disponible</div>
                  <div className="text-lg font-bold text-green-400">
                    {formatCurrency(selectedAccount.balance)}
                  </div>
                </div>
                <div className="bg-blue-500/10 border border-blue-500/30 p-3 rounded-lg">
                  <div className="text-sm text-muted">ID de Cuenta</div>
                  <div className="text-xs font-mono text-brand-blue">{selectedAccount.id}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-end">
        <Button
          variant="black"
          size="large"
          onClick={handleNext}
          disabled={!selectedAccount}
          className="min-w-32"
        >
          Siguiente
        </Button>
      </div>
    </div>
  );
};
